import { FunctionComponent, ReactNode } from "react";
import {
	faCheckCircle,
	faCircleMinus,
	faCircleXmark,
	faPlus,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

export type ChipStatus = "ok" | "warn" | "bad" | "off";

interface StatusChipProps {
	label: string;
	status: ChipStatus;
	selected: boolean;
	onClick: () => void;
	/** Hover card; shown only while the chip is not selected. */
	preview?: ReactNode;
	testId?: string;
}

const STATUS_ICON = {
	ok: { icon: faCheckCircle, className: "text-status-success" },
	warn: { icon: faTriangleExclamation, className: "text-status-warning" },
	bad: { icon: faCircleXmark, className: "text-status-error" },
	off: { icon: faCircleMinus, className: "text-text-disabled" },
} as const;

/**
 * A selectable chip with a status icon (PREC-4055): green check when complete,
 * amber triangle when it grades but leaves part of its value space unscored
 * (PREC-4127), red x when something is missing, grey minus and strike-through
 * when the row is deactivated. The segment, persona, scoring and criterion rows
 * all use it.
 */
export const StatusChip: FunctionComponent<StatusChipProps> = ({
	label,
	status,
	selected,
	onClick,
	preview,
	testId,
}) => {
	const { icon, className } = STATUS_ICON[status];

	const chip = (
		<button
			aria-pressed={selected}
			className={classNames(
				"flex h-[30px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors",
				selected
					? "border-brand-light bg-blue-action-200 font-semibold text-brand-light"
					: "border-border-light bg-white text-text hover:border-gray-300 hover:bg-background-light",
				status === "off" && !selected && "bg-background-light text-text-disabled line-through"
			)}
			data-testid={testId}
			type="button"
			onClick={onClick}
		>
			<Icon className={classNames("h-3 w-3", className)} icon={icon} />
			{label}
		</button>
	);

	if (preview && !selected) {
		return (
			<Tooltip panel content={preview} delay={350} direction="bottom">
				{chip}
			</Tooltip>
		);
	}

	return chip;
};

/** The "add" chip at the end of a chip row. */
export const AddChip: FunctionComponent<{
	label: string;
	onClick: () => void;
	testId?: string;
}> = ({ label, onClick, testId }) => (
	<button
		className="flex h-[30px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-blue-action bg-blue-action px-3 text-xs font-semibold text-white transition-colors hover:bg-blue-action-dark"
		data-testid={testId}
		type="button"
		onClick={onClick}
	>
		<Icon className="h-3 w-3" icon={faPlus} />
		{label}
	</button>
);
